import { Injectable } from '@angular/core';
import { AuthentificationType } from '../oidc-security.service';
import { Preferences } from '@capacitor/preferences';

type StorageKeys = 'access_token' | 'refresh_token' | 'id_token' | 'userData' | 'authStateControl' | 'codeVerifier';

@Injectable({ providedIn: 'root' })
export class OidcStorageService {
  private async read(key: StorageKeys, type: AuthentificationType): Promise<any> {
    let read = await Preferences.get({
      key: type + '_' + key,
    });
    return read.value || undefined;
  }

  private async write(key: StorageKeys, value: any, type: AuthentificationType): Promise<void> {
    await Preferences.set({
      key: type + '_' + key,
      value: value,
    });
  }

  private async remove(key: StorageKeys, type: AuthentificationType): Promise<void> {
    await Preferences.remove({
      key: type + '_' + key,
    });
  }

  async clear(type: AuthentificationType): Promise<void> {
    await this.remove('access_token', type);
    await this.remove('id_token', type);
    await this.remove('refresh_token', type);
    await this.remove('userData', type);
    await this.remove('authStateControl', type);
    await this.remove('codeVerifier', type);
  }

  async getAccessToken(type: AuthentificationType): Promise<string> {
    return await this.read('access_token', type);
  }

  async getIdToken(type: AuthentificationType): Promise<string> {
    return await this.read('id_token', type);
  }

  async getRefreshToken(type: AuthentificationType): Promise<string> {
    return await this.read('refresh_token', type);
  }

  async getAuthStateControl(type: AuthentificationType): Promise<string> {
    return this.read('authStateControl', type);
  }

  async setAuthStateControl(accessToken: string, type: AuthentificationType): Promise<void> {
    return await this.write('authStateControl', accessToken, type);
  }

  async getCodeVerifier(type: AuthentificationType): Promise<string> {
    return this.read('codeVerifier', type);
  }

  async setCodeVerifier(accessToken: string, type: AuthentificationType): Promise<void> {
    return await this.write('codeVerifier', accessToken, type);
  }

  async setAccessToken(accessToken: string, type: AuthentificationType): Promise<void> {
    return await this.write('access_token', accessToken, type);
  }

  async setRefreshToken(refreshToken: string, type: AuthentificationType): Promise<void> {
    if (refreshToken && refreshToken !== '') return await this.write('refresh_token', refreshToken, type);
  }

  async setIdToken(token: string, type: AuthentificationType): Promise<void> {
    return await this.write('id_token', token, type);
  }

  async getUserDataFromStore(type: AuthentificationType): Promise<any> {
    return JSON.parse((await this.read('userData', type)) || null);
  }

  async setUserDataToStore(userData: any, type: AuthentificationType): Promise<void> {
    await this.write('userData', JSON.stringify(userData), type);
  }
}
